"use client";

import {
  Field,
  FieldDescription,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import type { ContactInfo } from "@/lib/products/types";

type ContactKey = "firstName" | "lastName" | "email" | "companyName";

interface ContactFieldSpec {
  key: ContactKey;
  label: string;
  autoComplete: string;
  placeholder?: string;
  type?: "email";
  optional?: boolean;
}

const FIRST_NAME: ContactFieldSpec = {
  key: "firstName",
  label: "First name",
  autoComplete: "given-name",
  placeholder: "Jane",
};
const LAST_NAME: ContactFieldSpec = {
  key: "lastName",
  label: "Last name",
  autoComplete: "family-name",
  placeholder: "Smith",
};
const COMPANY: ContactFieldSpec = {
  key: "companyName",
  label: "Company",
  autoComplete: "organization",
  optional: true,
};
const EMAIL: ContactFieldSpec = {
  key: "email",
  label: "Email",
  autoComplete: "email",
  placeholder: "jane@company.com",
  type: "email",
};

const COMPANY_HINT = {
  sell: "Leave blank if you're selling for yourself",
  recycle: "Leave blank if you're recycling for yourself",
} as const;

export function PublicListingContactFields({
  contact,
  onChange,
  intent,
}: {
  contact: ContactInfo;
  onChange: (updates: Partial<ContactInfo>) => void;
  intent: "sell" | "recycle";
}) {
  const fieldProps = { contact, onChange };
  return (
    <div className="grid gap-5">
      <div className="grid gap-5 sm:grid-cols-2 sm:gap-4">
        <ContactField spec={FIRST_NAME} {...fieldProps} />
        <ContactField spec={LAST_NAME} {...fieldProps} />
      </div>
      <ContactField spec={COMPANY} hint={COMPANY_HINT[intent]} {...fieldProps} />
      <ContactField spec={EMAIL} {...fieldProps} />
    </div>
  );
}

function ContactField({
  spec,
  hint,
  contact,
  onChange,
}: {
  spec: ContactFieldSpec;
  hint?: string;
  contact: ContactInfo;
  onChange: (updates: Partial<ContactInfo>) => void;
}) {
  return (
    <Field controlId={spec.key} className="gap-2">
      <FieldLabel className="text-sm text-foreground" optional={spec.optional}>
        {spec.label}
      </FieldLabel>
      <Input
        id={spec.key}
        type={spec.type ?? "text"}
        name={spec.autoComplete}
        autoComplete={spec.autoComplete}
        placeholder={spec.placeholder}
        value={contact[spec.key] ?? ""}
        onChange={(event) => onChange({ [spec.key]: event.target.value })}
        className="text-base"
      />
      {hint ? <FieldDescription className="text-sm">{hint}</FieldDescription> : null}
    </Field>
  );
}
